// Top bar — the new app-wide header (2026-05-08 redesign).
//
// Replaces the old centered "TrailerCast™ / AI Sales Experience"
// title-card header with a sales-tool-shaped top bar:
//
//   [Company logo] [Company name]   [🔍 Search...]   [🔔]   [Avatar]
//
// Design decisions locked with the user:
//   - Search scope: universal (demos / calls / spaces / team)
//   - Logo fallback: TrailerCast SVG when company.brand.logoUrl is unset
//   - Tagline ("AI Sales Experience"): dropped — belongs on the
//     marketing site, not the app
//   - Mobile pattern: search collapses to a magnifying-glass icon that
//     opens a full-screen overlay
//   - Logo click: navigates to dashboard
//
// Notifications + profile-avatar buttons stay in App.jsx (they own
// large modal state — opening them from a TopBar prop would mean
// threading 8+ state setters through). The TopBar accepts a
// `rightSlot` and renders whatever JSX the parent passes.

import { useEffect, useMemo, useRef, useState } from 'react';
import { displayCompanyName } from '../utils/displayCompanyName';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

// Default TrailerCast crosshair icon — used when the company hasn't
// uploaded a brand logo yet.
function TrailerCastDefaultLogo({ size = 32 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" />
      <circle cx="24" cy="24" r="8" fill="currentColor" />
      <path d="M24 4 L24 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M24 36 L24 44" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M4 24 L12 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M36 24 L44 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

// Magnifying-glass icon used in both the desktop input prefix and the
// mobile collapsed icon button.
function SearchIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

// Tiny per-result-type pill so the dropdown groups read at a glance.
function TypePill({ type }) {
  const palette = {
    demo:  { label: 'Demo',  bg: '#f3e8ff', fg: '#9333ea' },
    call:  { label: 'Call',  bg: '#cffafe', fg: '#0e7490' },
    space: { label: 'Space', bg: '#fce7f3', fg: '#be185d' },
    team:  { label: 'Team',  bg: '#dbeafe', fg: '#1d4ed8' },
  };
  const p = palette[type] || { label: type, bg: 'var(--room-border-soft, #f1f5f9)', fg: 'var(--room-text-body, #475569)' };
  return (
    <span style={{
      display: 'inline-block',
      fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: p.fg, background: p.bg,
      padding: '2px 7px', borderRadius: 100,
      flexShrink: 0,
    }}>
      {p.label}
    </span>
  );
}

/**
 * Search input + results dropdown. Used as both the desktop inline
 * widget and the mobile full-screen overlay (the only thing that
 * differs between the two is the wrapper position/styling).
 */
function SearchWidget({ authToken, autoFocus = false, onResultClick, mobile = false }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  // Anti-autofill defenses (2026 Chrome no longer respects
  // autoComplete="off" on text/search inputs, and on this page the
  // logged-in user's email is visible nearby — Chrome's heuristic
  // happily prefills it as a "search query" on every nav).
  //
  //  1. Randomized name per mount → Chrome's per-(url + name) autofill
  //     memory has nothing to match against on each re-render.
  //  2. readOnly on first paint, cleared on first focus/hover → defeats
  //     the autofill pass Chrome runs against newly-rendered inputs.
  //     We never set readOnly back, so this only costs the user a
  //     microsecond on mount.
  const randomName = useMemo(
    () => `q_${Math.random().toString(36).slice(2, 10)}`,
    []
  );
  const [readOnly, setReadOnly] = useState(true);
  const releaseReadOnly = () => { if (readOnly) setReadOnly(false); };

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // autoFocus paths (mobile overlay) need the field interactive
      // immediately, so release the readOnly guard before focusing.
      setReadOnly(false);
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Debounced search. 250ms felt right in testing — fast enough for
  // streaming feedback, slow enough that typing "brightside" doesn't
  // fire 10 round-trips.
  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setLoading(true);
      fetch(`${API_BASE}/api/search?q=${encodeURIComponent(trimmed)}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
        .then((r) => r.ok ? r.json() : { results: [] })
        .then((data) => {
          if (cancelled) return;
          setResults(Array.isArray(data.results) ? data.results : []);
        })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [q, authToken]);

  // Group results by type for grouped rendering. Preserves insertion
  // order from the backend (demos first, then calls, etc).
  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const r of results) {
      if (!buckets.has(r.type)) buckets.set(r.type, []);
      buckets.get(r.type).push(r);
    }
    return [...buckets.entries()];
  }, [results]);

  const handleResultClick = (result) => {
    setOpen(false);
    setQ('');
    if (onResultClick) onResultClick(result);
    else if (result.href) window.location.assign(result.href);
  };

  // Close the dropdown when the user clicks anywhere outside the input.
  // Capture-phase so a click that triggers a result still navigates
  // (the result row's onMouseDown fires before this blur).
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative', flex: 1, maxWidth: mobile ? '100%' : 480 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'var(--room-border-soft, #f1f5f9)',
        border: '1px solid var(--room-border, #e2e8f0)',
        borderRadius: 8,
        padding: '8px 12px',
        transition: 'border-color 120ms ease, background 120ms ease',
      }}>
        <span style={{ color: 'var(--room-text-muted, #64748b)', display: 'flex', alignItems: 'center' }}>
          <SearchIcon size={16} />
        </span>
        {/* Honeypot inputs: Chrome's autofill picks the FIRST text and
            email-shaped field it sees on render. Putting two off-screen
            decoys above the real input gives Chrome something to fill
            instead of the search bar. Belt-and-suspenders alongside the
            readOnly + randomized name tricks above — needed because
            Chrome has tightened autofill aggressively against e-commerce
            sites and the heuristic now fires on plain text inputs near
            visible email strings (avatar tooltip / welcome text). */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          tabIndex={-1}
          aria-hidden="true"
          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: 0, width: 0, left: -9999 }}
        />
        <input
          type="password"
          name="password"
          autoComplete="current-password"
          tabIndex={-1}
          aria-hidden="true"
          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: 0, width: 0, left: -9999 }}
        />
        <input
          ref={inputRef}
          // Anti-autofill stack (Chrome ignores autoComplete="off" on
          // plain text/search inputs in 2026, especially when an email
          // string is visible nearby in the DOM):
          //   - autoComplete="new-password" — Chrome's strongest signal
          //     to skip autofill suggestions on a non-password field
          //   - name={randomName} — different per mount so Chrome's
          //     per-(url + name) autofill memory has no match
          //   - readOnly initially → released on focus/hover → defeats
          //     the autofill pass Chrome runs against fresh inputs
          //   - hidden honeypot username/password inputs above absorb
          //     the autofill that would otherwise hit this input
          type="search"
          name={randomName}
          autoComplete="new-password"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          inputMode="search"
          readOnly={readOnly}
          data-lpignore="true"
          data-1p-ignore="true"
          data-form-type="other"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => { releaseReadOnly(); setOpen(true); }}
          onMouseEnter={releaseReadOnly}
          placeholder="Search demos, calls, spaces, teammates…"
          style={{
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            fontSize: 14, fontFamily: 'inherit', color: 'var(--room-text-strong, #0f172a)',
            // Hide the native search-input UA decorations (the X button
            // in WebKit) since we render our own clear button below.
            WebkitAppearance: 'none',
            appearance: 'none',
          }}
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(''); setResults([]); inputRef.current?.focus(); }}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--room-text-subtle, #94a3b8)', padding: 2, lineHeight: 1, fontSize: 16,
            }}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: 'var(--room-card, #ffffff)',
          border: '1px solid var(--room-border, #e2e8f0)',
          borderRadius: 10,
          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12), 0 4px 12px rgba(15, 23, 42, 0.06)',
          maxHeight: 480, overflowY: 'auto',
          zIndex: 200,
        }}>
          {loading && results.length === 0 && (
            <div style={{ padding: 16, color: 'var(--room-text-muted, #64748b)', fontSize: 13 }}>Searching…</div>
          )}
          {!loading && results.length === 0 && (
            <div style={{ padding: 16, color: 'var(--room-text-muted, #64748b)', fontSize: 13 }}>
              No matches for <strong style={{ color: 'var(--room-text-strong, #0f172a)' }}>{q.trim()}</strong>
            </div>
          )}
          {grouped.map(([type, items]) => (
            <div key={type} style={{ padding: '6px 0' }}>
              <div style={{
                padding: '4px 14px', fontSize: 11, fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                color: 'var(--room-text-subtle, #94a3b8)',
              }}>
                {type === 'demo' ? 'Demos' : type === 'call' ? 'Calls' : type === 'space' ? 'Buyer Spaces' : 'Team'}
              </div>
              {items.map((r) => (
                <button
                  key={`${type}-${r.id}`}
                  type="button"
                  onClick={() => handleResultClick(r)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    width: '100%', padding: '8px 14px',
                    border: 'none', background: 'transparent',
                    cursor: 'pointer', textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--room-card-hover, #f8fafc)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <TypePill type={type} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 14, fontWeight: 500, color: 'var(--room-text-strong, #0f172a)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      {r.isPrivate && <span title="Private" style={{ fontSize: 12 }}>🔒</span>}
                      {r.label}
                    </div>
                    {r.sublabel && (
                      <div style={{
                        fontSize: 12, color: 'var(--room-text-muted, #64748b)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {r.sublabel}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Mobile full-screen search overlay. Triggered from the magnifying-
 * glass icon in the collapsed top bar. Backdrop click + Esc close.
 *
 * onResultClick is forwarded from the parent <TopBar> so search
 * results dispatch through the same SPA-aware navigation handler
 * as the desktop dropdown — without it the overlay would fall back
 * to window.location.assign(href) which doesn't trigger phase
 * switches in the hash-routed shell.
 */
function MobileSearchOverlay({ authToken, onClose, onResultClick }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15, 23, 42, 0.5)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--room-card, #ffffff)',
          padding: '14px 16px',
          display: 'flex', alignItems: 'center', gap: 10,
          borderBottom: '1px solid var(--room-border, #e2e8f0)',
        }}
      >
        <SearchWidget
          authToken={authToken}
          autoFocus
          onResultClick={(r) => {
            onClose();
            if (onResultClick) onResultClick(r);
            else if (r.href) window.location.assign(r.href);
          }}
          mobile
        />
        <button
          type="button"
          onClick={onClose}
          style={{
            border: 'none', background: 'transparent', cursor: 'pointer',
            fontSize: 14, fontWeight: 500, color: 'var(--room-text-strong, #0f172a)',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * The top bar itself. Receives `rightSlot` from App.jsx where the
 * notifications-bell + profile-avatar render with their existing
 * modal state intact.
 */
export default function TopBar({ authUser, authToken, onLogoClick, onResultClick, rightSlot }) {
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const brandLogoUrl = authUser?.company?.brand?.logoUrl || null;
  // Sanitize so the __pending__<email> placeholder created during
  // signup never leaks to the header. Falls back to 'TrailerCast'
  // (product name) when the user hasn't picked a company name yet.
  const rawCompany = authUser?.company?.name || authUser?.companyName || null;
  const companyName = displayCompanyName(rawCompany, 'TrailerCast');

  return (
    <>
      <header
        style={{
          display: 'flex', alignItems: 'center', gap: 16,
          padding: isMobile ? '10px 14px' : '12px 24px',
          background: 'var(--room-card, #ffffff)',
          borderBottom: '1px solid var(--room-border, #e2e8f0)',
          height: 60,
          flexShrink: 0,
        }}
      >
        {/* Logo + company name. Clickable → dashboard. */}
        <button
          type="button"
          onClick={onLogoClick}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: 0, fontFamily: 'inherit',
          }}
        >
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 40, height: 40,
            // No background pill behind a brand logo — logos own their
            // own visual treatment. Default TrailerCast SVG keeps the
            // gradient pill so the fallback feels intentional, not bare.
            borderRadius: brandLogoUrl ? 0 : 10,
            background: brandLogoUrl ? 'transparent' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: 'white', overflow: 'hidden', flexShrink: 0,
          }}>
            {brandLogoUrl
              ? (
                <img
                  src={brandLogoUrl}
                  alt=""
                  // Explicit width/height HTML attributes (not just CSS) so
                  // the browser knows the box size before CSS parses or
                  // before React's inline style resolves. Without these the
                  // img briefly rendered at the data URL's natural decoded
                  // dimensions on first paint, then snapped to 40×40 once
                  // layout settled — visible as a flash during page load.
                  width={40}
                  height={40}
                  style={{
                    width: '100%', height: '100%',
                    objectFit: 'contain', display: 'block',
                  }}
                />
              )
              : <TrailerCastDefaultLogo size={26} />}
          </span>
          {!isMobile && (
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--room-text-strong, #0f172a)', letterSpacing: '-0.01em' }}>
              {companyName}
            </span>
          )}
        </button>

        {/* Search input — desktop only, signed-in only.
            Hidden on the auth screens (sign-in / signup / forgot
            password) since there's nothing to search until the user
            has a session. Spacer kept so the right side stays
            right-aligned. */}
        {!isMobile && authUser && (
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <SearchWidget authToken={authToken} onResultClick={onResultClick} />
          </div>
        )}

        {(isMobile || !authUser) && (
          <div style={{ flex: 1 }} />
        )}

        {/* Mobile search icon — also signed-in only. */}
        {isMobile && authUser && (
          <button
            type="button"
            onClick={() => setMobileSearchOpen(true)}
            aria-label="Search"
            style={{
              width: 40, height: 40, borderRadius: 10,
              border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #ffffff)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: 'var(--room-text-strong, #0f172a)',
            }}
          >
            <SearchIcon size={18} />
          </button>
        )}

        {/* Notifications + profile slot — owned by App.jsx. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {rightSlot}
        </div>
      </header>

      {mobileSearchOpen && (
        <MobileSearchOverlay
          authToken={authToken}
          onClose={() => setMobileSearchOpen(false)}
          onResultClick={onResultClick}
        />
      )}
    </>
  );
}
